import type { SheetSpriteDef, SheetStrip, SpriteAnimState } from './types';

/** States that repeat until the orchestrator moves the piece elsewhere. */
const LOOPING_STATES: ReadonlySet<SpriteAnimState> = new Set([
  'resting',
  'hover',
  'moving',
]);

export function isLoopingState(state: SpriteAnimState): boolean {
  return LOOPING_STATES.has(state);
}

/** Resolve the strip for a state, falling back to resting when unauthored. */
export function pickStrip(
  def: SheetSpriteDef,
  state: SpriteAnimState
): SheetStrip {
  return def.strips[state] ?? def.strips.resting;
}

export type FrameRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/**
 * Pixel rect of one frame within its strip. Indices outside the strip are
 * clamped so a truncated download can never produce an out-of-bounds texture.
 */
export function stripFrameRect(
  def: SheetSpriteDef,
  strip: SheetStrip,
  frameIndex: number
): FrameRect {
  const lastIndex = Math.max(0, strip.frameCount - 1);
  const clamped = Math.min(Math.max(Math.trunc(frameIndex), 0), lastIndex);
  return {
    x: clamped * def.frameWidth,
    y: 0,
    width: def.frameWidth,
    height: def.frameHeight,
  };
}

export type FrameCursor = {
  frameIndex: number;
  accumulatedMs: number;
};

export type AdvanceFrameInput = FrameCursor & {
  elapsedMs: number;
  frameCount: number;
  fps: number;
  loop: boolean;
};

export type AdvanceFrameResult = FrameCursor & {
  /** True when `frameIndex` changed and the texture needs swapping. */
  changed: boolean;
};

/**
 * Fixed-timestep frame advance. Kept pure so playback timing can be verified
 * without a renderer; `PieceNode` owns the texture swap this drives.
 */
export function advanceFrame({
  frameIndex,
  accumulatedMs,
  elapsedMs,
  frameCount,
  fps,
  loop,
}: AdvanceFrameInput): AdvanceFrameResult {
  const atEnd = frameIndex >= frameCount - 1;
  if (frameCount <= 1 || fps <= 0 || elapsedMs <= 0 || (!loop && atEnd)) {
    return { frameIndex, accumulatedMs, changed: false };
  }

  const interval = 1000 / fps;
  let index = frameIndex;
  let accum = accumulatedMs + elapsedMs;
  let changed = false;

  while (accum >= interval) {
    accum -= interval;
    index = loop
      ? (index + 1) % frameCount
      : Math.min(index + 1, frameCount - 1);
    changed = true;
    if (!loop && index >= frameCount - 1) {
      accum = 0;
      break;
    }
  }

  return { frameIndex: index, accumulatedMs: accum, changed };
}

/** Wall-clock duration of one full pass of a strip, in milliseconds. */
export function stripDurationMs(strip: SheetStrip): number {
  if (strip.fps <= 0) return 0;
  return (strip.frameCount / strip.fps) * 1000;
}
